import { ssoClient } from "@better-auth/sso/client";
import { log } from "@grafit/shared/observability/sentry-front";
import type { Auth, BetterAuthOptions } from "better-auth";
import type { BetterAuthClientOptions } from "better-auth/client";
import { adminClient, customSessionClient, emailOTPClient, magicLinkClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { z } from "zod";
import { getBaseUrl } from "./utils/get-base-url";
import { clearSSOState } from "./utils/sso-state.ts";
import { clearLoginDate } from "./utils/store-last-login.ts";

async function DONT_USE_ME_I_M_TYPING_ONLY_authConfig() {
  // WARN: this is ugly and only for getting the type without importing shared-api-router, improve that
  const { admin, customSession, emailOTP, magicLink } = await import("better-auth/plugins");
  const { sso } = await import("@better-auth/sso");

  const DONT_USE_ME_I_M_TYPING_ONLY_options = {
    emailAndPassword: {
      enabled: false,
    },
    baseURL: "",
    trustedOrigins: [""],
    secret: "",
    plugins: [
      sso(),
      admin(),
      emailOTP({
        async sendVerificationOTP() {
          await Promise.resolve();
        },
      }),
      magicLink({
        disableSignUp: true,
        sendMagicLink: async () => {
          await Promise.resolve();
        },
      }),
    ],
  } satisfies BetterAuthOptions;

  return {
    ...DONT_USE_ME_I_M_TYPING_ONLY_options,
    plugins: [
      ...DONT_USE_ME_I_M_TYPING_ONLY_options.plugins,
      customSession(async ({ user, session }) => {
        await Promise.resolve();
        return {
          signedRoles: "" as string,
          phoneNumber: "" as string,
          jobTitle: "" as string,
          trigram: "" as string,
          user,
          session,
        };
      }, DONT_USE_ME_I_M_TYPING_ONLY_options),
    ],
  } satisfies BetterAuthOptions;
}

const authClientOptions = {
  baseURL: getBaseUrl(),
  sessionOptions: {
    refetchOnWindowFocus: false,
  },
  plugins: [
    ssoClient(),
    customSessionClient<Auth<Awaited<ReturnType<typeof DONT_USE_ME_I_M_TYPING_ONLY_authConfig>>>>(),
    emailOTPClient(),
    magicLinkClient(),
    adminClient(),
  ],
} satisfies BetterAuthClientOptions;

type AuthClient = ReturnType<typeof createAuthClient<typeof authClientOptions>>;

export const authClient: AuthClient = createAuthClient(authClientOptions);

const SignOutResultSchema = z
  .object({
    error: z.unknown().nullable().optional(),
  })
  .loose();

const ErrorLikeSchema = z.object({ message: z.string() }).loose();

function toSignOutError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  const parsedError = ErrorLikeSchema.safeParse(error);
  return new Error(parsedError.success ? parsedError.data.message : "Sign out failed");
}

let signOutInFlight: Promise<void> | null = null;

export function isSigningOut(): boolean {
  return signOutInFlight !== null;
}

export function authSignOut(options?: { onSuccess?: () => void; skipSSOCleanup?: boolean }): Promise<void> {
  if (signOutInFlight) {
    return signOutInFlight;
  }

  signOutInFlight = authClient
    .signOut({
      fetchOptions: {
        onSuccess: () => {
          clearLoginDate();
          if (!options?.skipSSOCleanup) {
            clearSSOState();
          }
          options?.onSuccess?.();
        },
      },
    })
    .then((result) => {
      const parsedResult = SignOutResultSchema.safeParse(result);
      if (parsedResult.success && parsedResult.data.error) {
        throw toSignOutError(parsedResult.data.error);
      }
      return undefined;
    })
    .catch((error: unknown) => {
      log(`[Auth SignOut] Tenant switch sign-out failed: ${String(error)}`, "warn");
    })
    .finally(() => {
      signOutInFlight = null;
      window.location.replace("/sign-in");
    });

  return signOutInFlight;
}
